import {
  ADVISOR_ENGAGEMENT_SOURCE_AI,
  ADVISOR_ENGAGEMENT_SOURCE_TEAM,
} from '@src/constants/advisor-engagement-sources'
import {
  ADVISOR_ENGAGEMENT_STATUS_ACTIVE,
  ADVISOR_ENGAGEMENT_STATUS_ENDED,
  ADVISOR_ENGAGEMENT_STATUS_INVITED,
  ADVISOR_ENGAGEMENT_STATUS_SEARCHING,
} from '@src/constants/advisor-engagement-statuses'
import { ScoAdvisorEngagement } from '@src/graphql-types'

export const END_BOARD_SEAT_CONFIRM =
  'End this board seat? Chat history is kept, but the seat will no longer be active.'

export const WITHDRAW_BOARD_INVITE_CONFIRM =
  'Withdraw this invite? The seat will stay open so you can invite someone else.'

export const DECLINE_BOARD_INVITE_CONFIRM = 'Decline this board seat?'

export type EngagementLifecycle = {
  isFounder: boolean
  isAdvisor: boolean
  isAI: boolean
  isTeam: boolean
  canEditSeat: boolean
  canWithdrawInvite: boolean
  canEndSeat: boolean
  canRespondToInvite: boolean
  canOpenChat: boolean
  canFindAdvisors: boolean
  canReviewApplications: boolean
  applicationCount: number
  waitingOnLabel: string | null
}

export const engagementLifecycle = (
  engagement: ScoAdvisorEngagement,
  viewerUserId?: string | null,
  advisorDisplayName?: string | null
): EngagementLifecycle => {
  const isFounder = !!viewerUserId && viewerUserId === engagement.founderUserId
  const isAdvisor =
    !!viewerUserId &&
    (viewerUserId === engagement.advisorUserId || viewerUserId === engagement.advisorUser?.userId)
  const isAI = !!engagement.isAI || engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_AI
  const isTeam = engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM
  const statusKey = engagement.statusKey
  const canEditSeat = isFounder && engagement.currentUserCanEdit !== false && !isAI && !isTeam
  const applicationCount = engagement.candidateCount ?? 0
  const isSearching = statusKey === ADVISOR_ENGAGEMENT_STATUS_SEARCHING
  const hasAdvisor = !!(engagement.advisorUserId || engagement.advisorUser)

  let waitingOnLabel: string | null = null
  if (statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED) {
    if (isAdvisor) {
      waitingOnLabel = 'Waiting on you to accept or decline'
    } else if (isFounder) {
      waitingOnLabel = `Waiting on ${advisorDisplayName?.trim() || 'the advisor'} to accept`
    }
  } else if (isSearching && isFounder && applicationCount > 0) {
    waitingOnLabel =
      applicationCount === 1
        ? '1 application waiting — review on the seat'
        : `${applicationCount} applications waiting — review on the seat`
  }

  return {
    isFounder,
    isAdvisor,
    isAI,
    isTeam,
    canEditSeat,
    canWithdrawInvite: canEditSeat && statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED,
    canEndSeat: canEditSeat && statusKey !== ADVISOR_ENGAGEMENT_STATUS_ENDED,
    canRespondToInvite: isAdvisor && statusKey === ADVISOR_ENGAGEMENT_STATUS_INVITED && !isAI,
    canOpenChat: !isAI && statusKey === ADVISOR_ENGAGEMENT_STATUS_ACTIVE,
    canFindAdvisors: isFounder && isSearching && !hasAdvisor,
    canReviewApplications: isFounder && isSearching && !hasAdvisor && applicationCount > 0,
    applicationCount,
    waitingOnLabel,
  }
}
